import React, { useEffect, useState } from "react";
import axios from "axios";
import baseURL from "../config";
import Navbar from "../Navbar/Navbar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Paper,
  Button,
  Dialog,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";

const UserList = () => {
  const [userType, setUserType] = useState("admin");
  const [groupedUsers, setGroupedUsers] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${baseURL}/api/getBSMUserList`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const allUsers = response.data.users || [];
      const grouped = allUsers.reduce((acc, user) => {
        const type = user.userType || "unknown";
        if (!acc[type]) acc[type] = [];
        acc[type].push(user);
        return acc;
      }, {});
      setGroupedUsers(grouped);
    } catch (error) {
      console.error("Error fetching users:", error);
      setGroupedUsers({});
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, userType]);

  const handleOpenDialog = (user) => {
    setSelectedUser(user);
    setEmailVerified(user.emailVerified);
    setOpenDialog(true);
  };
  const handleDialogSubmit = async () => {
    if (!selectedUser) return;

    const token = localStorage.getItem("token"); // Get the token from localStorage

    if (!token) {
      toast.error("No token found, please login.");
      return;
    }

    try {
      await axios.put(
        `${baseURL}/api/updateUserBSE`,  // API endpoint
        {
          userId: selectedUser._id,  // Pass the selected user's ID in the body
          username: selectedUser.username,  // Updated fields
          emailVerified,
          userType: selectedUser.userType,
          executiveId: selectedUser.executiveId,
          approved: selectedUser.approved,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`, // Add token to headers for authorization
          },
        }
      );
      setOpenDialog(false);
      setSelectedUser(null);
      fetchUsers(); // Refetch users after update
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update user.");
    }
  };



  const currentUsers = groupedUsers[userType] || [];
  const filteredUsers = currentUsers.filter((user) =>
    user.username?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const availableUserTypes = Object.keys(groupedUsers);

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom color="primary">
          👥 Team BSM
        </Typography>

        <Tabs
          value={userType}
          onChange={(e, val) => setUserType(val)}
          variant="scrollable"
          scrollButtons="auto"
          textColor="secondary"
          indicatorColor="secondary"
          sx={{
            mb: 3,
            ".MuiTabs-indicator": {
              height: 4,
              borderRadius: 2,
              backgroundImage: "linear-gradient(to right, #1976d2, #42a5f5)",
            },
            ".MuiTab-root": {
              textTransform: "capitalize",
              fontWeight: 600,
              fontSize: 16,
              px: 2,
            },
          }}
        >
          {availableUserTypes.map((type) => (
            <Tab key={type} label={type.replace("_", " ")} value={type} />
          ))}
        </Tabs>

        <TextField
          placeholder="🔍 Search users by name"
          variant="outlined"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 4, background: "#fff", borderRadius: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        <Paper
          elevation={3}
          sx={{
            borderRadius: 4,
            background: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
            p: 2,
          }}
        >
          {loading ? (
            <Box textAlign="center" py={5}>
              <CircularProgress />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Typography textAlign="center">
              {currentUsers.length === 0
                ? "No users available."
                : "No users match your search."}
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f1f5f9" }}>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((user, index) => (
                      <TableRow
                        key={user._id}
                        hover
                        sx={{
                          transition: "all 0.2s ease-in-out",
                          ":hover": { backgroundColor: "#f9fafb" },
                        }}
                      >
                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: "#1976D2" }}>
                              <PersonIcon />
                            </Avatar>
                            <Box>
                              <Typography fontWeight={600}>
                                {user.username}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {user.userType}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              user.emailVerified ? "Verified" : "Not Verified"
                            }
                            color={user.emailVerified ? "success" : "warning"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {["admin", "BSE", "team_leader"].includes(
                            user.userType
                          ) &&
                            user.userType !== "vessel_owner" && (
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleOpenDialog(user)}
                              >
                                Update
                              </Button>
                            )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box mt={2}>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredUsers.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                />
              </Box>
            </>
          )}
        </Paper>
      </Container>

      {/* Dialog for Update */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Update User</DialogTitle>
        <DialogContent>
          <Typography fontWeight={600}>{selectedUser?.username}</Typography>

          {/* Username field */}
          <TextField
            label="Username"
            value={selectedUser?.username}
            onChange={(e) => setSelectedUser({ ...selectedUser, username: e.target.value })}
            fullWidth
            margin="normal"
          />

          {/* Email Verified Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={emailVerified}
                onChange={(e) => setEmailVerified(e.target.checked)}
              />
            }
            label="Email Verified"
          />

          {/* UserType Dropdown */}
          <TextField
            select
            label="User Type"
            value={selectedUser?.userType || ""}
            onChange={(e) => setSelectedUser({ ...selectedUser, userType: e.target.value })}
            fullWidth
            margin="normal"
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="BSE">BSE</MenuItem>
            <MenuItem value="team_leader">Team Leader</MenuItem>
            {/* Add more user types as needed */}
          </TextField>



          {/* Approved Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedUser?.approved || false}
                onChange={(e) => setSelectedUser({ ...selectedUser, approved: e.target.checked })}
              />
            }
            label="Approved"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDialogSubmit}
            variant="contained"
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </>
  );
};

export default UserList;
